// Text-region-like inspector for training
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { API_URL } from '../utils/apiConfig';
import { getTextInRegion } from '../api/documents';
import { FORM_FIELD_TYPES } from '../components/SignatureFieldPlacement';
import { trainingService } from '../services/trainingService';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function TrainingEditorPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [numPages, setNumPages] = React.useState(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [selection, setSelection] = React.useState(null);
  const [isSelecting, setIsSelecting] = React.useState(false);
  const [startPt, setStartPt] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const overlayRef = React.useRef(null);
  const [fields, setFields] = React.useState([]); // saved selections
  const [selectedFieldId, setSelectedFieldId] = React.useState(null);

  // Controls: field type and assignee
  const [mode, setMode] = React.useState('signature'); // 'signature' | 'form'
  const [formType, setFormType] = React.useState('name');
  const [assignee, setAssignee] = React.useState('me'); // 'me' | 'person2'..'person5'

  const token = React.useMemo(() => localStorage.getItem('token'), []);
  const fileSource = React.useMemo(() => ({
    url: `${API_URL}/documents/${documentId}/file?skipEmbed=true`,
    httpHeaders: token ? { Authorization: `Bearer ${token}` } : {}
  }), [documentId, token]);

  const onDocLoad = ({ numPages }) => setNumPages(numPages);

  const relPoint = (evt) => {
    const rect = overlayRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, evt.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, evt.clientY - rect.top));
    return { x, y };
  };

  const onDown = (e) => {
    if (!overlayRef.current) return;
    const pt = relPoint(e);
    setIsSelecting(true);
    setStartPt(pt);
    setSelection({ x: pt.x, y: pt.y, width: 0, height: 0 });
    setResult(null);
    // Starting a new selection should create a new field when saved
    setSelectedFieldId(null);
  };
  const onMove = (e) => {
    if (!isSelecting || !startPt) return;
    const pt = relPoint(e);
    const x = Math.min(startPt.x, pt.x);
    const y = Math.min(startPt.y, pt.y);
    const width = Math.abs(pt.x - startPt.x);
    const height = Math.abs(pt.y - startPt.y);
    setSelection({ x, y, width, height });
  };
  const onUp = async () => {
    if (!isSelecting) return;
    setIsSelecting(false);
    if (!selection || selection.width < 2 || selection.height < 2) return;
    try {
      setLoading(true);
      const res = await getTextInRegion(documentId, {
        page: pageNumber,
        x: Math.round(selection.x),
        y: Math.round(selection.y),
        width: Math.round(selection.width),
        height: Math.round(selection.height),
        tolerance: 3
      });
      setResult(res.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.error || 'Failed to extract text' });
    } finally {
      setLoading(false);
    }
  };

  const clearSel = () => { setSelection(null); setResult(null); setSelectedFieldId(null); };

  const currentType = mode === 'signature' ? 'signature' : formType;

  const addOrUpdateField = () => {
    if (!selection || !selection.width || !selection.height) return;
    const data = {
      id: selectedFieldId || `f_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
      page: pageNumber,
      x: Math.round(selection.x),
      y: Math.round(selection.y),
      width: Math.round(selection.width),
      height: Math.round(selection.height),
      type: currentType,
      slot: assignee,
      preview: {
        text: result?.text || '',
        context: result?.context || {}
      }
    };
    setFields(prev => {
      const exists = prev.some(f => f.id === data.id);
      if (exists) {
        return prev.map(f => f.id === data.id ? data : f);
      }
      return [...prev, data];
    });
    setSelectedFieldId(data.id);
  };

  const deleteSelected = () => {
    if (!selectedFieldId) return;
    setFields(prev => prev.filter(f => f.id !== selectedFieldId));
    setSelectedFieldId(null);
    clearSel();
  };

  const selectField = (field) => {
    setSelectedFieldId(field.id);
    setMode(field.type === 'signature' ? 'signature' : 'form');
    if (field.type !== 'signature') setFormType(field.type);
    setAssignee(field.slot || 'me');
    // load selection box
    setSelection({ x: field.x, y: field.y, width: field.width, height: field.height });
    // we won't refetch text automatically; user can drag/update to refresh
    setResult(field.preview || null);
    if (field.page !== pageNumber) setPageNumber(field.page);
  };

  const submitTraining = async () => {
    if (fields.length === 0) return;
    try {
      setLoading(true);
      const payloadFields = fields.map(f => ({
        type: f.type,
        page: f.page,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        slot: f.slot
      }));
      const meta = {
        source: 'training-editor-ui',
        selectionsCount: fields.length
      };
      const res = await trainingService.saveManualFields(documentId, payloadFields, meta);
      alert(`Saved ${res.recordsCreated || fields.length} training records`);
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || 'Failed to save training info');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <button
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white"
            onClick={() => navigate('/training')}
          >
            ← Back to Training
          </button>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-sm text-gray-300">Page {pageNumber}{numPages ? ` / ${numPages}` : ''}</div>
            <button className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700" onClick={() => setPageNumber(p => Math.max(1, p - 1))}>Prev</button>
            <button className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700" onClick={() => setPageNumber(p => Math.min(numPages || p + 1, p + 1))}>Next</button>
            <button className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700" onClick={clearSel}>Clear</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded shadow overflow-auto p-4">
              <Document file={fileSource} onLoadSuccess={onDocLoad} loading={<div className="text-gray-400">Loading PDF…</div>}>
                <div className="relative inline-block">
                  <Page pageNumber={pageNumber} scale={1.0} renderAnnotationLayer={false} renderTextLayer={false} />
                  <div
                    ref={overlayRef}
                    className="absolute inset-0 cursor-crosshair"
                    onMouseDown={onDown}
                    onMouseMove={onMove}
                    onMouseUp={onUp}
                    onMouseLeave={onUp}
                    style={{ zIndex: 10 }}
                  >
                    {selection && (
                      <div className="absolute bg-blue-500/20 border border-blue-400" style={{ left: selection.x, top: selection.y, width: selection.width, height: selection.height }} />
                    )}
                    {/* Saved fields overlay for this page */}
                    {fields.filter(f => f.page === pageNumber).map(f => (
                      <div
                        key={f.id}
                        className={`absolute ${selectedFieldId===f.id?'border-2 border-amber-400':'border border-green-400'} bg-green-500/10 hover:bg-green-500/20`}
                        style={{ left: f.x, top: f.y, width: f.width, height: f.height, cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); selectField(f); }}
                        title={`${f.type} • ${f.slot}`}
                      />
                    ))}
                  </div>
                </div>
              </Document>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded shadow p-4 h-full flex flex-col gap-4">
              <div>
                <div className="text-sm text-gray-300 mb-2">Mode</div>
                <div className="flex gap-2">
                  <button className={`px-3 py-1.5 rounded ${mode==='signature'?'bg-blue-600':'bg-gray-700'}`} onClick={()=>setMode('signature')}>Signature</button>
                  <button className={`px-3 py-1.5 rounded ${mode==='form'?'bg-blue-600':'bg-gray-700'}`} onClick={()=>setMode('form')}>Form field</button>
                </div>
              </div>

              {mode==='form' && (
                <div>
                  <div className="text-sm text-gray-300 mb-2">Form field type</div>
                  <select className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" value={formType} onChange={(e)=>setFormType(e.target.value)}>
                    {Object.keys(FORM_FIELD_TYPES).map(k => (
                      <option key={k} value={k}>{FORM_FIELD_TYPES[k].label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <div className="text-sm text-gray-300 mb-2">Assign to</div>
                <select className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" value={assignee} onChange={(e)=>setAssignee(e.target.value)}>
                  <option value="me">Me</option>
                  <option value="person2">Person 2</option>
                  <option value="person3">Person 3</option>
                  <option value="person4">Person 4</option>
                  <option value="person5">Person 5</option>
                </select>
              </div>

              <div>
                <div className="text-sm text-gray-300 mb-2">Selected region</div>
                <div className="text-xs text-gray-300">{selection ? `x:${Math.round(selection.x)}, y:${Math.round(selection.y)}, w:${Math.round(selection.width)}, h:${Math.round(selection.height)}` : 'Drag on the page to select a region'}</div>
              </div>

              <div className="flex-1 overflow-auto">
                <div className="text-sm text-gray-300 mb-2">Captured text</div>
                {loading && <div className="text-gray-400">Extracting…</div>}
                {!loading && result?.error && <div className="text-red-400 text-sm">{result.error}</div>}
                {!loading && result && !result.error && (
                  <>
                    <pre className="whitespace-pre-wrap text-xs text-gray-100 bg-gray-900/40 p-3 rounded mb-3">{result.text || ''}</pre>
                    <div className="text-sm text-gray-300 mb-1">Context</div>
                    <div className="text-xs text-gray-400 mb-1">Above</div>
                    <pre className="whitespace-pre-wrap text-xs text-gray-200 bg-gray-900/30 p-2 rounded mb-2">{result.context?.above || ''}</pre>
                    <div className="text-xs text-gray-400 mb-1">On</div>
                    <pre className="whitespace-pre-wrap text-xs text-gray-200 bg-gray-900/30 p-2 rounded mb-2">{result.context?.on || ''}</pre>
                    <div className="text-xs text-gray-400 mb-1">Below</div>
                    <pre className="whitespace-pre-wrap text-xs text-gray-200 bg-gray-900/30 p-2 rounded">{result.context?.below || ''}</pre>
                  </>
                )}
              </div>

              <div className="mt-3 flex gap-2">
                <button className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-500" onClick={addOrUpdateField} disabled={!selection}> {selectedFieldId ? 'Update Field' : 'Add Field'} </button>
                <button className="px-3 py-2 rounded bg-red-600 hover:bg-red-500 disabled:opacity-50" onClick={deleteSelected} disabled={!selectedFieldId}>Delete</button>
                <button className="ml-auto px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50" onClick={submitTraining} disabled={fields.length===0 || loading}>Submit ({fields.length})</button>
              </div>

              <div className="mt-4">
                <div className="text-sm text-gray-300 mb-2">Fields</div>
                <div className="space-y-2 max-h-64 overflow-auto">
                  {fields.length === 0 && <div className="text-xs text-gray-500">No fields added yet.</div>}
                  {fields.map(f => (
                    <div key={f.id} className={`p-2 rounded border ${selectedFieldId===f.id?'border-amber-400 bg-amber-500/10':'border-gray-700 bg-gray-900/30'} cursor-pointer`} onClick={() => selectField(f)}>
                      <div className="text-xs text-gray-200 flex justify-between">
                        <span>{f.type} • {f.slot}</span>
                        <span>p{f.page} ({f.x},{f.y},{f.width}×{f.height})</span>
                      </div>
                      {f.preview?.text && <div className="mt-1 text-[11px] text-gray-400 truncate">{f.preview.text}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrainingEditorPage;
